import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquarePlus } from 'lucide-react';
import { createContext, useContext, useState, type FormEvent } from 'react';
import { Button, cn, ErrorBanner, Label, Modal, Textarea } from '../components/ui.js';
import {
  errorMessage,
  trpc,
  trpcClient,
  type RouterInputs,
  type RouterOutputs,
} from '../lib/trpc.js';

/**
 * "Ask for a change" on the owner dashboard's cards: the person says in their own words what
 * they would like changed, and the request is kept in Settings and, where the deploy forwards
 * them, sent to the team that looks after the system. Only the card's name goes with it, never
 * what the card shows.
 */

export type CardKind = RouterInputs['dashboard']['askForChange']['kind'];

/** The view being looked at, for the signed-in app only; null where nothing can be asked. */
export interface AskTarget {
  view: string;
  /** The request goes to the team that runs the deploy, not only into Settings. */
  forwarded: boolean;
  /** How many characters the core takes. */
  max_words: number;
}

export const AskContext = createContext<AskTarget | null>(null);

const WHERE = 'You’ll find it in Settings, under Changes asked for.';

/** What the dialog says once the request is in, by the request's status. */
const DONE: Record<
  RouterOutputs['dashboard']['askForChange']['status'],
  { title: string; text: string }
> = {
  kept: { title: 'Request saved', text: `Your request is saved. ${WHERE}` },
  sending: {
    title: 'Request sent',
    text: `Your request is on its way to the team that looks after this system. ${WHERE}`,
  },
  sent: {
    title: 'Request sent',
    text: `Your request has reached the team that looks after this system. ${WHERE}`,
  },
  not_sent: {
    title: 'Request saved',
    text: `Your request is saved, but it couldn’t be sent to the team that looks after this system, so let them know another way. ${WHERE}`,
  },
};

export function AskForChange({
  kind,
  cardKey,
  label,
  text = 'Ask for a change',
  className,
}: {
  kind: CardKind;
  /** The card's key in its view; null for the whole view and the owner's number. */
  cardKey: string | null;
  /** What the card is called, for the dialog's title. */
  label: string;
  text?: string;
  className?: string;
}) {
  const target = useContext(AskContext);
  const [open, setOpen] = useState(false);
  if (!target) return null;
  const testId = `ask-${kind}${cardKey ? `-${cardKey}` : ''}`;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        data-testid={testId}
        className={cn(
          'inline-flex items-center gap-1 text-xs font-medium text-brand-strong hover:underline',
          className,
        )}
      >
        <MessageSquarePlus className="size-3" aria-hidden />
        {text}
      </button>
      {open && (
        <AskDialog
          target={target}
          kind={kind}
          cardKey={cardKey}
          label={label}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function AskDialog({
  target,
  kind,
  cardKey,
  label,
  onClose,
}: {
  target: AskTarget;
  kind: CardKind;
  cardKey: string | null;
  label: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [words, setWords] = useState('');
  const ask = useMutation({
    mutationFn: () =>
      trpcClient.dashboard.askForChange.mutate({
        view: target.view,
        kind,
        key: cardKey,
        words,
        // Only the app draws a client's own card, so only it knows the card's title.
        ...(kind === 'card' && label.trim() !== '' && { title: label }),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: trpc.dashboard.changeRequests.queryKey() }),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    ask.mutate();
  };
  const sent = ask.data;
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={sent ? DONE[sent.status].title : `Ask for a change: ${label}`}
    >
      {sent ? (
        <div className="space-y-4" data-testid="ask-done">
          <p className="text-sm text-ink-2">{DONE[sent.status].text}</p>
          <Button onClick={onClose}>Close</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          {ask.error && <ErrorBanner>{errorMessage(ask.error)}</ErrorBanner>}
          <div>
            <Label htmlFor="ask-words">What would you like changed?</Label>
            <Textarea
              id="ask-words"
              rows={4}
              required
              maxLength={target.max_words}
              autoFocus
              placeholder="For example: split this by technician."
              value={words}
              onChange={(e) => setWords(e.target.value)}
            />
          </div>
          <p className="text-xs text-ink-3">
            {target.forwarded
              ? 'It goes to the team that looks after this system, with your name, your email and which card it’s about. None of the numbers or records on the card are sent.'
              : 'It’s kept in Settings, under Changes asked for, where your administrators can see it.'}
          </p>
          <div className="flex gap-2">
            <Button type="submit" busy={ask.isPending} disabled={words.trim() === ''}>
              Send
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
